import { useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import { ASSETS_BUCKET, createSignedAssetUrl, type StorageBucket } from '../../lib/storage';

export interface SignedAssetUrlResult {
  url: string | null;
  isLoading: boolean;
  error: string | null;
}

/** Result of the last completed signing, tagged with the object it belongs to. */
interface SignedResult {
  key: string;
  url: string | null;
  error: string | null;
}

/** Two buckets hold objects with the same shape of key, so the tag carries both. */
function signedKey(bucket: StorageBucket, storagePath: string): string {
  return `${bucket}/${storagePath}`;
}

/**
 * Signs a private Storage object for preview.
 *
 * Both buckets are private, so images are never referenced by public URL — each
 * preview gets a short-lived signed URL (cached per bucket + path in
 * `lib/storage`). State is keyed by that pair and derived during render, so a
 * path change never shows the previous file's URL.
 */
export function useSignedAssetUrl(
  storagePath: string | null,
  bucket: StorageBucket = ASSETS_BUCKET,
): SignedAssetUrlResult {
  const [signed, setSigned] = useState<SignedResult | null>(null);

  useEffect(() => {
    if (!storagePath) return;

    const key = signedKey(bucket, storagePath);
    let cancelled = false;

    void (async () => {
      try {
        const url = await createSignedAssetUrl(storagePath, bucket);
        if (cancelled) return;
        setSigned({ key, url, error: null });
      } catch (caught) {
        if (cancelled) return;
        setSigned({
          key,
          url: null,
          error: getErrorMessage(caught, 'Preview unavailable.'),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bucket, storagePath]);

  const isCurrent =
    signed !== null && storagePath !== null && signed.key === signedKey(bucket, storagePath);

  return {
    url: isCurrent ? signed.url : null,
    isLoading: storagePath !== null && !isCurrent,
    error: isCurrent ? signed.error : null,
  };
}
